/**
 * NewCustomerModal (NEWCUST-3, Seth 2026-06-26) — turn an UNMATCHED inbound lead
 * (web form / chat / voice) into a brand-new Pegasus customer + their first
 * dispatch, both prefilled from the same info. The "lead → customer → job"
 * funnel (ServiceTitan / HubSpot). Dedup FIRST so we never create a second copy
 * of someone already in ESC or Pegasus ([[feedback_multi_signal_identity_match]]).
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { UserPlus, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useEntryFormKeys } from "@/lib/keys";
import { supabase } from "@/lib/supabase";
import { useNavigateWithBack } from "@/lib/navBack";
import { mapUrgency, mapWindow, looksLikeWebForm } from "@/lib/intakeMap";
import { CreateDispatchModal } from "@/features/customer-360/CreateDispatchModal";
import { useConversationMessages, type InboxConversation } from "./api";

/** Pull a "Label: value" line out of a web-form body. */
function formField(body: string, label: string): string {
  const m = body.match(new RegExp(`^\\s*${label}\\s*:\\s*(.+)$`, "im"));
  return m ? m[1].trim() : "";
}

function splitName(full: string): { first: string; last: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length <= 1) return { first: full.trim(), last: "" };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1] };
}

export function NewCustomerModal({
  conversation,
  onClose,
}: {
  conversation: InboxConversation;
  onClose: () => void;
}) {
  const navigate = useNavigateWithBack();
  const { data: messages } = useConversationMessages(
    conversation.custno,
    conversation.endpoint,
  );

  // The lead's structured content: prefer the most recent inbound web-form
  // message; fall back to the conversation's contact name + phone.
  const leadBody = useMemo(() => {
    const inbound = (messages ?? []).filter((m) => m.direction === "inbound");
    const form = [...inbound].reverse().find((m) => looksLikeWebForm(m.body));
    return form?.body ?? [...inbound].reverse()[0]?.body ?? "";
  }, [messages]);

  // CHAT-PREFILL (Seth 2026-08-28): a plain chat has no Label: value form —
  // mine the whole inbound conversation for an email / phone so "New customer"
  // from any chat still arrives mostly filled out.
  const mined = useMemo(() => {
    const text = (messages ?? []).filter((m) => m.direction === "inbound").map((m) => m.body ?? "").join("\n");
    return {
      email: text.match(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/)?.[0] ?? "",
      phone: text.match(/\(?\b\d{3}\)?[\s.\-]?\d{3}[\s.\-]?\d{4}\b/)?.[0] ?? "",
    };
  }, [messages]);

  const seed = useMemo(() => {
    const isForm = looksLikeWebForm(leadBody);
    const nameFromForm = isForm ? formField(leadBody, "Name") : "";
    const full =
      nameFromForm || conversation.provider_contact_name || "";
    const { first, last } = splitName(full);
    return {
      first,
      last,
      email: (isForm ? formField(leadBody, "Email") : "") || mined.email,
      phone:
        (isForm ? formField(leadBody, "Phone") : "") ||
        (conversation.channel === "sms" ? conversation.endpoint ?? "" : "") ||
        mined.phone,
      add1: isForm ? formField(leadBody, "Address") : "",
      city: isForm ? formField(leadBody, "City") : "",
      state: isForm ? formField(leadBody, "State") : "",
      zip: isForm ? formField(leadBody, "Zip") || formField(leadBody, "Zip Code") : "",
    };
  }, [leadBody, conversation, mined]);

  const [first, setFirst] = useState(seed.first);
  const [last, setLast] = useState(seed.last);
  const [phone, setPhone] = useState(seed.phone);
  const [email, setEmail] = useState(seed.email);
  const [add1, setAdd1] = useState(seed.add1);
  const [city, setCity] = useState(seed.city);
  const [statev, setStatev] = useState(seed.state);
  const [zip, setZip] = useState(seed.zip);
  // CUST-BILL-01: separate billing address (property manager / landlord / builder).
  const [billOpen, setBillOpen] = useState(false);
  const [billName, setBillName] = useState("");
  const [billAdd1, setBillAdd1] = useState("");
  const [billCity, setBillCity] = useState("");
  const [billState, setBillState] = useState("");
  const [billZip, setBillZip] = useState("");
  // Re-seed once the messages load (the form opens before the fetch resolves).
  const [seeded, setSeeded] = useState(false);
  if (!seeded && (seed.first || seed.phone || seed.add1)) {
    setSeeded(true);
    setFirst(seed.first); setLast(seed.last); setPhone(seed.phone);
    setEmail(seed.email); setAdd1(seed.add1); setCity(seed.city);
    setStatev(seed.state); setZip(seed.zip);
  }

  // Dedup — phone + address against ESC + Pegasus.
  const { data: dupes } = useQuery({
    queryKey: ["cust_dupes", phone, add1, city],
    enabled: !!(phone.trim() || add1.trim()),
    queryFn: async () => {
      const { data } = await supabase.rpc("find_customer_matches", {
        p_phone: phone, p_add1: add1, p_city: city,
      });
      return (data ?? []) as { custno: string; name: string; address: string; src: string; match_on: string }[];
    },
  });

  const [booking, setBooking] = useState<null | { custno: string }>(null);
  const create = useMutation({
    mutationFn: async () => {
      const full = `${first} ${last}`.trim();
      // Best-guess the dispatch fields from the lead while we're here.
      let summary = "";
      let urgency: string | undefined;
      let win: string | undefined;
      let typeName: string | undefined;
      if (leadBody.trim()) {
        try {
          const { data } = await supabase.functions.invoke("summarize-intake", { body: { text: leadBody } });
          const d = data as { ok?: boolean; summary?: string; fields?: { urgency?: string; promised_window?: string; suggested_service_type?: string } } | null;
          if (d?.ok) { summary = d.summary ?? ""; urgency = d.fields?.urgency; win = d.fields?.promised_window; typeName = d.fields?.suggested_service_type; }
        } catch { /* prefill is best-effort */ }
      }
      const { data: newNo, error } = await supabase.rpc("create_pegasus_customer", {
        p_first: first, p_last: last, p_full: full,
        p_phone: phone, p_email: email,
        p_add1: add1, p_city: city, p_state: statev, p_zip: zip,
        p_source: conversation.channel ?? "web",
        p_conversation_key: conversation.conversation_key,
        p_bill_name: billOpen ? billName.trim() || null : null,
        p_bill_add1: billOpen ? billAdd1.trim() || null : null,
        p_bill_city: billOpen ? billCity.trim() || null : null,
        p_bill_state: billOpen ? billState.trim() || null : null,
        p_bill_zip: billOpen ? billZip.trim() || null : null,
      });
      if (error) throw error;
      return { custno: String(newNo), summary, urgency, win, typeName };
    },
  });

  const onFormKeys = useEntryFormKeys(() => { if (!create.isPending && (first.trim() || last.trim())) void onSubmit(); });
  const onSubmit = async () => {
    const r = await create.mutateAsync();
    // Chain straight into the dispatch builder, prefilled with the same info.
    setBooking({ custno: r.custno });
    (window as unknown as { __newCustPrefill?: unknown }).__newCustPrefill = r;
  };

  // After the customer is made, render the dispatch builder with the form values
  // directly (a Pegasus custno has no ESC mirror row to prefill from).
  if (booking) {
    const r = (window as unknown as { __newCustPrefill?: { summary?: string; urgency?: string; win?: string; typeName?: string } }).__newCustPrefill ?? {};
    return (
      <CreateDispatchModal
        custno={booking.custno}
        locno="00001"
        customerName={`${first} ${last}`.trim() || "New customer"}
        address={add1}
        city={city}
        phone={phone}
        initialDescription={r.summary ?? ""}
        sourceCaption={r.summary ? "AI-filled from the web request" : null}
        prefillUrgency={mapUrgency(r.urgency)}
        prefillWindow={mapWindow(r.win)}
        prefillTypeName={r.typeName || null}
        onClose={() => { onClose(); navigate(`/customers/${booking.custno}`); }}
      />
    );
  }

  const input = "w-full rounded-md border border-hairline px-2 py-1.5 text-sm";
  const label = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400";

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="New Customer"
      subtitle="Create a brand-new customer from this lead, then book their first visit"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">Cancel</button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={create.isPending || !(first.trim() || last.trim())}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
            {create.isPending ? "Creating…" : "Create customer & dispatch"}
            {!create.isPending && <ArrowRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      }
    >
      <div className="space-y-3" data-entry-form onKeyDown={onFormKeys}>
        {dupes && dupes.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <div className="flex items-center gap-1.5 font-semibold">
              <AlertTriangle className="h-3.5 w-3.5" /> Might already be a customer — check before creating a duplicate:
            </div>
            <ul className="mt-1 space-y-0.5">
              {dupes.slice(0, 4).map((d) => (
                <li key={d.custno}>
                  <button
                    type="button"
                    onClick={() => { onClose(); navigate(`/customers/${d.custno}`); }}
                    className="text-left underline-offset-2 hover:underline"
                  >
                    {d.name} · {d.address || d.custno} <span className="text-amber-700">({d.src}, matched {d.match_on})</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs"><span className={label}>First name</span><input className={input} value={first} onChange={(e) => setFirst(e.target.value)} data-autofocus="" /></label>
          <label className="block text-xs"><span className={label}>Last name</span><input className={input} value={last} onChange={(e) => setLast(e.target.value)} /></label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs"><span className={label}>Phone</span><input className={input} value={phone} onChange={(e) => setPhone(e.target.value)} /></label>
          <label className="block text-xs"><span className={label}>Email</span><input className={input} value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        </div>
        <label className="block text-xs"><span className={label}>Street address</span><input className={input} value={add1} onChange={(e) => setAdd1(e.target.value)} /></label>
        <div className="grid grid-cols-3 gap-3">
          <label className="col-span-1 block text-xs"><span className={label}>City</span><input className={input} value={city} onChange={(e) => setCity(e.target.value)} /></label>
          <label className="block text-xs"><span className={label}>State</span><input className={input} value={statev} onChange={(e) => setStatev(e.target.value)} /></label>
          <label className="block text-xs"><span className={label}>Zip</span><input className={input} value={zip} onChange={(e) => setZip(e.target.value)} /></label>
        </div>
        {!billOpen ? (
          <button type="button" onClick={() => setBillOpen(true)} className="text-left text-[11px] font-semibold text-brand-600 underline-offset-2 hover:underline">
            + Billing is different from the service address (property manager, landlord, builder)
          </button>
        ) : (
          <div className="space-y-2 rounded-lg border border-hairline bg-slate-50/60 p-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Billing address</span>
              <button type="button" onClick={() => setBillOpen(false)} className="text-[11px] text-slate-400 hover:text-slate-600">same as service — remove</button>
            </div>
            <label className="block text-xs"><span className={label}>Bill to (name / company)</span><input className={input} value={billName} onChange={(e) => setBillName(e.target.value)} placeholder="e.g. Oakwood Property Management" /></label>
            <label className="block text-xs"><span className={label}>Street address</span><input className={input} value={billAdd1} onChange={(e) => setBillAdd1(e.target.value)} /></label>
            <div className="grid grid-cols-3 gap-3">
              <label className="col-span-1 block text-xs"><span className={label}>City</span><input className={input} value={billCity} onChange={(e) => setBillCity(e.target.value)} /></label>
              <label className="block text-xs"><span className={label}>State</span><input className={input} value={billState} onChange={(e) => setBillState(e.target.value)} /></label>
              <label className="block text-xs"><span className={label}>Zip</span><input className={input} value={billZip} onChange={(e) => setBillZip(e.target.value)} /></label>
            </div>
          </div>
        )}
        <p className="text-[11px] text-slate-400">
          This creates a Pegasus-native customer (number 3,000,001+). Their address becomes the service location; you can refine it on the 360 afterward.
        </p>
      </div>
    </Modal>
  );
}
